import {
  Box,
  CircularProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";

import AvatarDisplay from "../../../../components/display/AvatarDisplay";
import { fetchPetsTab } from "../../../../enums/queryFunction/Fetch";
import { PETS_GENERAL } from "../../../../enums/QueryKeys";
import { GENERAL } from "../../enums/TabsRequestParams";
import usePrimaryColor from "../../../../hooks/usePrimaryColor";
import usePetAvatar from "../../../../hooks/usePetAvatar";
import {
  getGenderAvatarTextColor,
  isUnknownGender,
  useUnknownGenderColor,
} from "../../utils/Functions";
import { petViewUrl } from "../../../../enums/UrlPaths";

function PetAvatarItem({ pet, getAvatarBgColor }) {
  const navigate = useNavigate();
  const { avatar, isLoading } = usePetAvatar(pet.id);
  const { mode } = useTheme().palette;

  return (
    <Tooltip title={pet.name || "Dog"}>
      <Avatar
        src={!isLoading ? avatar : undefined}
        onClick={(e) => {
          e.stopPropagation();
          navigate(petViewUrl(pet.id, pet.userId));
        }}
        sx={{
          width: 36,
          height: 36,
          cursor: "pointer",
          fontWeight: 700,
          fontSize: "0.9rem",
          bgcolor: !avatar ? getAvatarBgColor(pet) : undefined,
          color: getGenderAvatarTextColor(pet?.gender, mode, pet?.deceased),
          transition: "transform 0.25s ease, box-shadow 0.25s ease",
          "&:hover": {
            transform: "scale(1.15)",
            zIndex: 10,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          },
        }}
      >
        {isLoading ? (
          <CircularProgress size={16} />
        ) : !avatar ? (
          pet.name?.charAt(0).toUpperCase()
        ) : null}
      </Avatar>
    </Tooltip>
  );
}

export default function PetAvatar({
  addAvatar = true,
  isGroup = false,
  petList = [],
  size,
}) {
  const { id: petId } = useParams();

  const blueColor = usePrimaryColor();
  const pinkColor = "hotpink";
  const unknownGenderColor = useUnknownGenderColor();

  const getAvatarBgColor = (pet) =>
    Boolean(pet?.deceased)
      ? "gray"
      : isUnknownGender(pet?.gender)
        ? unknownGenderColor
        : String(pet?.gender).toUpperCase() === "MALE"
          ? blueColor
          : pinkColor;

  const { data, isLoading, error } = useQuery({
    queryKey: [PETS_GENERAL + petId],
    queryFn: () => fetchPetsTab(GENERAL, petId),
    staleTime: Infinity,
    enabled: !isGroup,
  });

  const { avatar, isLoading: loadingAvatar } = usePetAvatar(
    !isGroup ? petId : null,
  );

  if (!isGroup) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <AvatarDisplay
          img={avatar}
          loadingAvatar={loadingAvatar}
          name={error ? "Dog" : data?.name}
          gender={error ? "MALE" : data?.gender}
          deceased={error ? false : data?.deceased}
          icon={data?.type?.icon}
          addAvatar={addAvatar}
          size={size}
          testId="pet.details.avatar"
        />
      </Box>
    );
  }

  if (!petList?.length) return null;

  return (
    <AvatarGroup
      max={4}
      spacing={10}
      sx={{
        "& .MuiAvatarGroup-avatar": {
          width: 36,
          height: 36,
          fontSize: "0.9rem",
        },
      }}
    >
      {petList.map((pet) => (
        <PetAvatarItem
          key={pet.id}
          pet={pet}
          getAvatarBgColor={getAvatarBgColor}
        />
      ))}
    </AvatarGroup>
  );
}
